// Which currency an admin payout comes OUT of, which one it settles in,
// and what stands between a balance and the external rail (SM-499).
//
// ── the fact this module exists to stop being invisible ──────────────
//
// An admin's commission is not one balance. Since ledger 0025 every
// admin has a GOLD commission account AND a GREEN sibling, and fee
// distribution credits whichever the channel earned in. The external
// payout rail, meanwhile, is GOLD-only — not "GOLD for now" in the sense
// of a switch someone could flip, but by the epic's withdraw CEILING
// (SM-478 hardcodes and validates the withdrawal currency server-side;
// RED/GREEN rejection is a ceiling, not a feature flag).
//
// So the Admin Payouts screen was answering one question — "approve this
// payout?" — while silently assuming the answer to two others: what
// currency left, and what happens to the currencies that cannot leave.
// Every figure on it was drawn with the legacy single-currency renderer,
// which is the same as asserting GOLD without saying so.
//
// ── everything here is DERIVED, nothing is a second opinion ──────────
//
// The eligible set is computed from `CURRENCY_POLICY`'s immutable
// withdraw ceiling rather than written down as ["GOLD"]. The conversion
// route is read from the same matrix's `convertTo`. If a fourth currency
// ever ships, or GREEN ever gains a rail, this file needs no edit and
// the screen follows — which is the whole reason the matrix is data.
//
// The ledger remains the enforcement authority. Nothing here grants a
// payout; it only decides what the screen is allowed to claim.

import { CURRENCY_POLICY, POLICY_ORDER, routeLabel } from "@/components/currency/policy";
import { campaignCurrency, type CurrencyCode } from "@/lib/currency";

/**
 * Currencies the external payout rail can settle in — the withdraw
 * ceiling, read off the policy matrix.
 *
 * Today: GOLD alone. This is deliberately a LIST rather than a constant
 * so that "how many currencies can be paid out" is a question the code
 * can answer, and so a second one arriving is data rather than a
 * refactor.
 */
export const PAYOUT_CURRENCIES: readonly CurrencyCode[] = Object.freeze(
  POLICY_ORDER.filter((code) => CURRENCY_POLICY[code].ceiling.withdraw),
);

/** Every currency the console will show a payout verdict for, in registry order. */
export const PAYOUT_PANEL_CURRENCIES: readonly CurrencyCode[] = POLICY_ORDER;

export const isPayoutCurrency = (code: CurrencyCode): boolean =>
  PAYOUT_CURRENCIES.includes(code);

/**
 * How value in a currency reaches an external address.
 *
 *   direct   — the rail settles in it (GOLD).
 *   convert  — it cannot leave, but a permitted conversion reaches one
 *              that can (GREEN → GOLD). The route id is the audited one,
 *              the same string ledger records against the conversion.
 *   never    — no route out at all (RED). Not a missing feature; the
 *              matrix says value leaves GAME as GREEN, never as RED.
 */
export type PayoutRail =
  | { kind: "direct" }
  | { kind: "convert"; to: CurrencyCode; route: string; label: string }
  | { kind: "never" };

export function payoutRail(code: CurrencyCode): PayoutRail {
  const policy = CURRENCY_POLICY[code];
  if (policy.ceiling.withdraw) return { kind: "direct" };
  // The first permitted hop that lands somewhere withdrawable. Single
  // hop on purpose: a two-step route would be two audited conversions
  // and two rates, and presenting it as one path would hide a step an
  // operator is accountable for.
  const to = policy.convertTo.find((target) => CURRENCY_POLICY[target].ceiling.withdraw);
  if (!to) return { kind: "never" };
  const route = conversionRoute(code, to);
  return { kind: "convert", to, route, label: routeLabel(route) };
}

/** Why a currency has no rail of its own, in the matrix's own words. */
export function payoutLockReason(code: CurrencyCode): string | undefined {
  return CURRENCY_POLICY[code].lockReason.withdraw;
}

/** The audited route id for a pair — `GREEN_TO_GOLD`, ledger's spelling. */
export const conversionRoute = (from: CurrencyCode, to: CurrencyCode): string =>
  `${from}_TO_${to}`;

/**
 * The two currencies a payout row is about.
 *
 * `admin_withdrawals` carries no currency column: the request debits the
 * admin's GOLD commission account and the rail settles in GOLD, both by
 * ceiling. So an absent field is not unknown — it is the legacy single
 * currency, which is the G3 boundary rule this console applies to every
 * other pre-currency payload.
 *
 * `reported` says which it was, because those are different facts. A row
 * that NAMES its currencies is authoritative; a row that does not is
 * being read through a rule, and a screen that quietly promotes the
 * second to the first is how a wrong unit gets printed next to real
 * money the day the rail gains a second currency.
 */
export interface PayoutCurrencies {
  source: CurrencyCode;
  settlement: CurrencyCode;
  reported: boolean;
}

export function payoutCurrencies(row: {
  source_currency?: string;
  settlement_currency?: string;
}): PayoutCurrencies {
  const reported = Boolean(row.source_currency || row.settlement_currency);
  // Each side falls back independently: a row naming only one of them is
  // still telling the truth about that one.
  const source = campaignCurrency(row.source_currency);
  const settlement = row.settlement_currency
    ? campaignCurrency(row.settlement_currency)
    : railSettlementCurrency();
  return { source, settlement, reported };
}

/**
 * What the rail settles in when the row does not say.
 *
 * Exactly one eligible currency is the state this rail is in, and then
 * the answer is that currency. If a second is ever enabled, "the rail's
 * currency" stops being a well-formed question — and the honest fallback
 * is the legacy one (GOLD, G3), not whichever happens to sort first.
 */
function railSettlementCurrency(): CurrencyCode {
  return PAYOUT_CURRENCIES.length === 1 ? PAYOUT_CURRENCIES[0] : campaignCurrency(undefined);
}

/**
 * The conversion a payout implies, or null when it implies none.
 *
 * `permitted` is not decoration. A source/settlement pair the matrix
 * does not allow is a data fault — a row claiming value moved along a
 * route that does not exist — and the screen has to be able to say so
 * rather than rendering an arrow that legitimises it. Same posture as
 * `ceilingDisagreement()`.
 */
export interface PayoutConversion {
  route: string;
  label: string;
  permitted: boolean;
}

export function payoutConversion(
  source: CurrencyCode,
  settlement: CurrencyCode,
): PayoutConversion | null {
  if (source === settlement) return null;
  const route = conversionRoute(source, settlement);
  return {
    route,
    label: routeLabel(route),
    permitted: CURRENCY_POLICY[source].convertTo.includes(settlement),
  };
}

/**
 * Read a payout-currency filter value out of untrusted text (a URL
 * search param).
 *
 * Unlike the positions page's stake filter, EVERY platform currency is
 * accepted here even though only GOLD can settle a payout. "Where does
 * my Green commission pay out?" is a question an operator genuinely
 * asks, and the console's job is to answer it with the policy — the
 * empty result carries the explanation. Silently dropping the value
 * would answer it with a full, unfiltered queue instead, which reads as
 * "these are the Green payouts".
 */
export function parsePayoutCurrency(raw: unknown): CurrencyCode | undefined {
  return typeof raw === "string" &&
    (PAYOUT_PANEL_CURRENCIES as readonly string[]).includes(raw)
    ? (raw as CurrencyCode)
    : undefined;
}
